'use client'
import { ReviewInfoData, ReviewInfoSection } from '@/src/types/hotels/details'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import HotelInfoSections, { HotelInfoPolicyRow } from './hotelInfoSections'

export interface HotelInfoSectionsContainerProps {
    info: ReviewInfoData
    /** USP chips from roomContent (localized). */
    tags?: string[]
    /** Total rooms in the property (roomContent), or null. */
    numOfRooms?: number | null
    /** Total floors in the property (roomContent), or null. */
    numOfFloors?: number | null
    /** Render every section open, with no read-more. For pages entered from a search engine. */
    expandAll?: boolean
}

const ABOUT_COLLAPSED_SECTIONS = 2

// Logic layer: resolves section labels (i18n), builds the facility/policy view-models,
// property-facts + tags from roomContent, and owns the about-text expand toggle.
const HotelInfoSectionsContainer = ({
    info,
    tags = [],
    numOfRooms = null,
    numOfFloors = null,
    expandAll = false
}: HotelInfoSectionsContainerProps) => {
    const t = useTranslations('hotels')
    const [aboutExpanded, setAboutExpanded] = useState(false)

    const propertyFacts = useMemo<string[]>(() => {
        const facts: string[] = []
        if (numOfRooms != null) facts.push(t('propertyRooms', { count: numOfRooms }))
        if (numOfFloors != null) facts.push(t('propertyFloors', { count: numOfFloors }))
        return facts
    }, [numOfRooms, numOfFloors, t])

    const facilitySections = useMemo<ReviewInfoSection[]>(
        () =>
            [
                { key: 'topFeatures', title: t('topFeatures'), items: info.facilities.general },
                { key: 'servicesLabel', title: t('servicesLabel'), items: info.facilities.services },
                { key: 'activitiesLabel', title: t('activitiesLabel'), items: info.facilities.activities }
            ].filter(section => section.items.length > 0),
        [info.facilities, t]
    )

    const policyRows = useMemo<HotelInfoPolicyRow[]>(
        () =>
            (
                [
                    { label: t('policyCheckIn'), value: info.policies.checkIn },
                    { label: t('policyCheckOut'), value: info.policies.checkOut },
                    { label: t('policyCancellation'), value: info.policies.cancellation },
                    { label: t('policyChildren'), value: info.policies.child },
                    { label: t('policyPets'), value: info.policies.pet },
                    { label: t('policyMandatoryFees'), value: info.policies.mandatoryFees }
                ] as { label: string; value?: string }[]
            ).filter((row): row is HotelInfoPolicyRow => Boolean(row.value)),
        [info.policies, t]
    )

    // The SEO page has nothing to reveal on demand — a visitor arriving from a search
    // engine should see the content, and a crawler should find it in the markup.
    const showAll = expandAll || aboutExpanded
    const aboutSections = showAll ? info.about : info.about.slice(0, ABOUT_COLLAPSED_SECTIONS)
    const hasMoreAbout = !expandAll && info.about.length > ABOUT_COLLAPSED_SECTIONS

    return (
        <HotelInfoSections
            info={info}
            labels={{
                overview: t('overview'),
                location: t('locationLabel'),
                attractions: t('attractionsLabel'),
                hotelPolicies: t('hotelPolicies')
            }}
            tags={tags}
            propertyFacts={propertyFacts}
            aboutSections={aboutSections}
            hasMoreAbout={hasMoreAbout}
            aboutToggleLabel={aboutExpanded ? t('readLess') : t('readMore')}
            expandAll={expandAll}
            onToggleAbout={() => setAboutExpanded(prev => !prev)}
            facilitySections={facilitySections}
            policyRows={policyRows}
        />
    )
}

export { HotelInfoSectionsContainer }
export default HotelInfoSectionsContainer
